import '../../styles/restScreens.css';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { supabaseClient } from '../../config/supabase';
import { formatPhone } from '../../lib/phone';

const ACCOUNT_FIELDS = [
    ['account_name', 'Account name'],
    ['account_type', 'Account type'],
    ['customer_tier_id', 'Customer tier'],
    ['is_business', 'Business account'],
    ['card_on_file', 'Card on file'],
    ['senior', 'Senior'],
    ['requires_human_review', 'Requires human review'],
];

const CONTACT_FIELDS = [
    ['name', 'Name'],
    ['addresses', 'Addresses'],
    ['position', 'Position'],
    ['photo_url', 'Photo'],
    ['notes', 'Notes'],
    ['primary_account_id', 'Primary account'],
    ['requires_human_review', 'Requires human review'],
    ['payment_preferences', 'Payment preferences'],
    ['communication_preferences', 'Communication preferences'],
];

export function normalizePhone(value) {
    const digits = String(value || '').replace(/[^0-9]/g, '');
    return /^1[0-9]{10}$/.test(digits) ? digits.slice(1) : digits;
}

const E164_NANP = /^\+1[0-9]{10}$/;

// Mirrors crm_merge_records: survivor values first, normalized duplicates collapse,
// and a phone kept in several formats prefers its E.164 form.
export function combinedContactValues(first, second, field) {
    const kept = new Map();
    [...(first?.[field] || []), ...(second?.[field] || [])].forEach((value, index) => {
        const normalized = field === 'phones'
            ? normalizePhone(value)
            : value.trim().toLowerCase();
        const key = normalized ? `${field}:${normalized}` : `raw:${value}`;
        const current = kept.get(key);
        if (!current || (field === 'phones' && E164_NANP.test(value) && !E164_NANP.test(current.value))) {
            kept.set(key, { value, index });
        }
    });
    return [...kept.values()].sort((a, b) => a.index - b.index).map((entry) => entry.value);
}

function displayValue(field, value, accountsById) {
    if (field === 'primary_account_id' && value) {
        return accountsById.get(value)?.account_name || value;
    }
    if (value === null || value === undefined || value === '') return '—';
    if (typeof value === 'object') return JSON.stringify(value, null, 2);
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    return String(value);
}

function recordTitle(record, type) {
    return type === 'account' ? record?.account_name : record?.name;
}

export default function CrmDuplicateReview() {
    const [flags, setFlags] = useState([]);
    const [records, setRecords] = useState(new Map());
    const [accountsById, setAccountsById] = useState(new Map());
    const [selections, setSelections] = useState({});
    const [survivors, setSurvivors] = useState({});
    const [confirming, setConfirming] = useState(null);
    const [loading, setLoading] = useState(true);
    const [busyFlagId, setBusyFlagId] = useState(null);
    const [loadError, setLoadError] = useState('');
    const [actionErrors, setActionErrors] = useState({});

    const loadFlags = useCallback(async () => {
        setLoading(true);
        setLoadError('');
        const { data: nextFlags, error: flagsError } = await supabaseClient
            .from('crm_duplicate_flags')
            .select('id, org_id, record_type, record_id, likely_duplicate_of, match_reason, detected_at')
            .eq('status', 'pending')
            .order('detected_at', { ascending: false });
        if (flagsError) {
            setLoadError(`Could not load possible duplicates: ${flagsError.message}`);
            setFlags([]);
            setRecords(new Map());
            setLoading(false);
            return;
        }

        const accountIds = new Set();
        const contactIds = new Set();
        (nextFlags || []).forEach((flag) => {
            const ids = flag.record_type === 'account' ? accountIds : contactIds;
            ids.add(flag.record_id);
            ids.add(flag.likely_duplicate_of);
        });

        const [accountResult, contactResult] = await Promise.all([
            accountIds.size
                ? supabaseClient.from('accounts')
                    .select('id, account_name, account_type, customer_tier_id, is_business, card_on_file, senior, requires_human_review')
                    .in('id', [...accountIds])
                : Promise.resolve({ data: [], error: null }),
            contactIds.size
                ? supabaseClient.from('contacts')
                    .select('id, name, emails, phones, addresses, position, photo_url, notes, primary_account_id, requires_human_review, payment_preferences, communication_preferences, sms_transactional_consent, sms_marketing_consent, sms_opted_out_at')
                    .in('id', [...contactIds])
                : Promise.resolve({ data: [], error: null }),
        ]);
        const rowError = accountResult.error || contactResult.error;
        if (rowError) {
            setLoadError(`Possible duplicates loaded, but record details could not be read: ${rowError.message}`);
            setFlags(nextFlags || []);
            setRecords(new Map());
            setAccountsById(new Map());
            setLoading(false);
            return;
        }

        const byId = new Map([
            ...(accountResult.data || []).map((record) => [record.id, { ...record, record_type: 'account' }]),
            ...(contactResult.data || []).map((record) => [record.id, { ...record, record_type: 'contact' }]),
        ]);
        const linkedAccountIds = [...new Set(
            (contactResult.data || []).map((contact) => contact.primary_account_id).filter(Boolean)
        )];
        const { data: linkedAccounts, error: linkedAccountError } = linkedAccountIds.length
            ? await supabaseClient.from('accounts').select('id, account_name').in('id', linkedAccountIds)
            : { data: [], error: null };
        if (linkedAccountError) {
            setLoadError(`Possible duplicates loaded, but linked account names could not be read: ${linkedAccountError.message}`);
            setFlags(nextFlags || []);
            setRecords(byId);
            setAccountsById(new Map());
            setLoading(false);
            return;
        }

        setFlags(nextFlags || []);
        setRecords(byId);
        setAccountsById(new Map((linkedAccounts || []).map((account) => [account.id, account])));
        setSurvivors(Object.fromEntries((nextFlags || []).map((flag) => [flag.id, flag.record_id])));
        setSelections({});
        setLoading(false);
    }, []);

    useEffect(() => {
        loadFlags();
    }, [loadFlags]);

    const updateChoice = (flagId, field, value) => {
        setSelections((current) => ({
            ...current,
            [flagId]: { ...current[flagId], [field]: value },
        }));
    };

    const actOnFlag = async (flag, action) => {
        setBusyFlagId(flag.id);
        setActionErrors((current) => ({ ...current, [flag.id]: '' }));
        try {
            if (action === 'dismiss') {
                const { error } = await supabaseClient.rpc('crm_dismiss_duplicate_flag', {
                    p_flag_id: flag.id,
                });
                if (error) throw error;
            } else {
                const survivorId = survivors[flag.id] || flag.record_id;
                const mergedId = survivorId === flag.record_id
                    ? flag.likely_duplicate_of
                    : flag.record_id;
                const { error } = await supabaseClient.rpc('crm_merge_records', {
                    p_record_type: flag.record_type,
                    p_surviving_id: survivorId,
                    p_merged_id: mergedId,
                    p_field_resolutions: selections[flag.id] || {},
                });
                if (error) throw error;
            }
            setConfirming(null);
            await loadFlags();
        } catch (error) {
            setActionErrors((current) => ({
                ...current,
                [flag.id]: `Could not ${action === 'dismiss' ? 'dismiss' : 'merge'} this pair: ${error.message || String(error)}`,
            }));
        } finally {
            setBusyFlagId(null);
        }
    };

    const pendingCount = flags.length;
    const duplicateRows = useMemo(() => flags.map((flag) => {
        const first = records.get(flag.record_id);
        const second = records.get(flag.likely_duplicate_of);
        const survivorId = survivors[flag.id] || flag.record_id;
        const survivor = survivorId === flag.record_id ? first : second;
        const merged = survivorId === flag.record_id ? second : first;
        return { flag, first, second, survivor, merged, fields: flag.record_type === 'account' ? ACCOUNT_FIELDS : CONTACT_FIELDS };
    }), [flags, records, survivors]);

    if (loading) {
        return <div data-m6-layout="clients" data-m6-component="CrmDuplicateReview" className="p-8 text-center text-[var(--ink-muted)]">Loading possible duplicates…</div>;
    }

    return (
        <section data-m6-layout="clients" data-m6-component="CrmDuplicateReview" aria-labelledby="crm-duplicate-review-heading" className="space-y-4">
            <header className="flex flex-wrap items-end justify-between gap-3">
                <div>
                    <h2 id="crm-duplicate-review-heading" className="text-2xl font-bold text-[var(--ink)]">
                        Possible duplicates
                    </h2>
                    <p className="mt-1 text-sm text-[var(--ink-muted)]">
                        {pendingCount === 1 ? '1 pair needs review.' : `${pendingCount} pairs need review.`}
                        {' '}Choose the record that stays, then choose each remaining field value. Contact emails and phones are combined automatically.
                    </p>
                </div>
                <button
                    type="button"
                    onClick={loadFlags}
                    className="ia-hover-pulse rounded-full border border-[var(--selected-pill-border)] px-4 py-2 text-sm font-medium text-[var(--selected-pill-ink)]"
                >
                    Refresh list
                </button>
            </header>

            <div role="status" className="rounded-lg border border-amber-400/50 bg-amber-400/10 p-3 text-sm text-[var(--ink)]">
                Consent is merged automatically: any SMS STOP or opt-out wins, and all consent-history entries stay with the surviving contact.
            </div>

            {loadError && (
                <div role="alert" className="rounded-lg border border-red-500/50 bg-red-500/10 p-3 text-sm text-[var(--ink)]">
                    {loadError}
                </div>
            )}

            {!loadError && duplicateRows.length === 0 && (
                <div className="rounded-lg border border-[var(--surface-border)] bg-[var(--surface-raised)] p-8 text-center text-[var(--ink-muted)]">
                    No possible duplicates need review.
                </div>
            )}

            {duplicateRows.map(({ flag, first, second, survivor, merged, fields }) => (
                <article
                    key={flag.id}
                    className="rounded-lg border border-[var(--surface-border)] bg-[var(--surface-raised)] p-4"
                >
                    <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                        <div>
                            <h3 className="font-semibold text-[var(--ink)]">
                                {flag.record_type === 'account' ? 'Account' : 'Contact'} match
                            </h3>
                            <p className="text-xs text-[var(--ink-muted)]">
                                {flag.match_reason.replace('_', ' ')} · detected {new Date(flag.detected_at).toLocaleString()}
                            </p>
                        </div>
                        <div className="flex gap-2">
                            <button
                                type="button"
                                disabled={busyFlagId === flag.id}
                                onClick={() => actOnFlag(flag, 'dismiss')}
                                className="ia-hover-pulse rounded-full border border-[var(--surface-border)] px-3 py-1.5 text-sm text-[var(--ink)] disabled:opacity-50"
                            >
                                Not a duplicate
                            </button>
                            {confirming === flag.id ? (
                                <>
                                    <button
                                        type="button"
                                        disabled={busyFlagId === flag.id}
                                        onClick={() => actOnFlag(flag, 'merge')}
                                        className="ia-hover-pulse rounded-full border border-[var(--selected-pill-border)] px-3 py-1.5 text-sm font-semibold text-[var(--selected-pill-ink)] disabled:opacity-50"
                                    >
                                        {busyFlagId === flag.id ? 'Merging…' : 'Confirm merge'}
                                    </button>
                                    <button
                                        type="button"
                                        disabled={busyFlagId === flag.id}
                                        onClick={() => setConfirming(null)}
                                        className="rounded-full border border-[var(--surface-border)] px-3 py-1.5 text-sm text-[var(--ink)]"
                                    >
                                        Cancel
                                    </button>
                                </>
                            ) : (
                                <button
                                    type="button"
                                    disabled={!first || !second || busyFlagId === flag.id}
                                    onClick={() => setConfirming(flag.id)}
                                    className="ia-hover-pulse rounded-full border border-[var(--selected-pill-border)] px-3 py-1.5 text-sm font-semibold text-[var(--selected-pill-ink)] disabled:opacity-50"
                                >
                                    Merge records
                                </button>
                            )}
                        </div>
                    </div>

                    {(!first || !second) ? (
                        <div role="alert" className="rounded border border-red-500/50 p-3 text-sm text-[var(--ink)]">
                            This pair is no longer fully visible. Refresh the list or ask an organization admin to review it.
                        </div>
                    ) : (
                        <>
                            <div className="grid gap-3 md:grid-cols-2">
                                {[first, second].map((record) => (
                                    <label
                                        key={record.id}
                                        className={`cursor-pointer rounded-lg border p-3 ${
                                            (survivors[flag.id] || flag.record_id) === record.id
                                                ? 'border-[var(--selected-pill-border)] text-[var(--selected-pill-ink)]'
                                                : 'border-[var(--surface-border)] text-[var(--ink)]'
                                        }`}
                                    >
                                        <span className="flex items-center gap-2 font-semibold">
                                            <input
                                                type="radio"
                                                name={`survivor-${flag.id}`}
                                                value={record.id}
                                                checked={(survivors[flag.id] || flag.record_id) === record.id}
                                                onChange={() => setSurvivors((current) => ({
                                                    ...current,
                                                    [flag.id]: record.id,
                                                }))}
                                            />
                                            Keep this record: {recordTitle(record, flag.record_type) || 'Unnamed record'}
                                        </span>
                                        <span className="mt-1 block break-all text-xs text-[var(--ink-muted)]">
                                            {record.id}
                                        </span>
                                    </label>
                                ))}
                            </div>

                            {flag.record_type === 'contact' && (
                                <div className="mt-4 space-y-2 border-t border-[var(--surface-border)] pt-3 text-sm text-[var(--ink)]">
                                    {[
                                        ['emails', 'Email addresses'],
                                        ['phones', 'Phone numbers'],
                                    ].map(([field, label]) => (
                                        <div key={field}>
                                            <span className="font-medium">{label} (combined)</span>
                                            <span className="ml-2 break-words">
                                                {combinedContactValues(survivor, merged, field)
                                                    .map((value) => (field === 'phones' ? formatPhone(value, 'US') : value))
                                                    .join(', ') || '—'}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            )}

                            <div className="mt-4 space-y-3">
                                {fields.map(([field, label]) => {
                                    const choice = selections[flag.id]?.[field] || 'surviving';
                                    const chosenRecord = choice === 'merged' ? merged : survivor;
                                    return (
                                        <fieldset
                                            key={field}
                                            className="grid gap-2 border-t border-[var(--surface-border)] pt-3 md:grid-cols-[12rem_1fr_1fr]"
                                        >
                                            <legend className="sr-only">{label}</legend>
                                            <span className="text-sm font-medium text-[var(--ink)]">{label}</span>
                                            {[['surviving', survivor], ['merged', merged]].map(([source, record]) => (
                                                <label key={source} className="flex min-w-0 gap-2 text-sm text-[var(--ink)]">
                                                    <input
                                                        type="radio"
                                                        name={`${flag.id}-${field}`}
                                                        value={source}
                                                        checked={choice === source}
                                                        onChange={() => updateChoice(flag.id, field, source)}
                                                    />
                                                    <span className="min-w-0">
                                                        <span className="block text-xs text-[var(--ink-muted)]">
                                                            {source === 'surviving' ? 'Surviving record' : 'Merged record'}
                                                        </span>
                                                        <span className="block break-words whitespace-pre-wrap">
                                                            {displayValue(field, record?.[field], accountsById)}
                                                        </span>
                                                    </span>
                                                </label>
                                            ))}
                                            <span className="sr-only">Selected: {displayValue(field, chosenRecord?.[field], accountsById)}</span>
                                        </fieldset>
                                    );
                                })}
                            </div>

                            {flag.record_type === 'contact' && (
                                <p className="mt-3 text-xs text-[var(--ink-muted)]">
                                    SMS consent proof is preserved from the most recent consent record. An opt-out on either contact suppresses SMS for the survivor.
                                </p>
                            )}
                        </>
                    )}

                    {actionErrors[flag.id] && (
                        <p role="alert" className="mt-3 rounded border border-red-500/50 bg-red-500/10 p-2 text-sm text-[var(--ink)]">
                            {actionErrors[flag.id]}
                        </p>
                    )}
                </article>
            ))}
        </section>
    );
}
